import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaCommentAlt } from 'react-icons/fa';
import '../../styles/FeedbackButton.css';

function FeedbackButton({ onClick, className = '' }) {
    const [position, setPosition] = useState({ x: null, y: null });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [hasMoved, setHasMoved] = useState(false);
    const buttonRef = useRef(null);

    // Handle mouse move - update position
    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;
        
        setHasMoved(true); // Mark that movement has occurred
        
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Keep button within viewport
        const buttonWidth = 150;
        const buttonHeight = 50;
        const maxX = window.innerWidth - buttonWidth;
        const maxY = window.innerHeight - buttonHeight;
        
        const constrainedPosition = {
            x: Math.max(0, Math.min(newX, maxX)),
            y: Math.max(0, Math.min(newY, maxY))
        };
        
        setPosition(constrainedPosition);
    }, [isDragging, dragOffset]);

    // Handle mouse up - stop dragging
    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
        }
    }, [isDragging]);

    // Handle mouse down - start dragging
    const handleMouseDown = (e) => {
        if (e.button !== 0) return; // Only handle left mouse button
        
        const rect = buttonRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setIsDragging(true);
        setHasMoved(false); // Reset movement flag
        e.preventDefault();
    };

    // Handle click - only trigger if not dragging and didn't move
    const handleClick = (e) => {
        if (hasMoved || isDragging) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        onClick();
    };

    // Add global mouse event listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Style for positioned button - use CSS default when not dragged
    const positionStyle = position.x !== null && position.y !== null ? {
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: 'auto',
        bottom: 'auto'
    } : {};

    return (
        <button 
            ref={buttonRef}
            className={`feedback-button ${className} ${isDragging ? 'dragging' : ''}`}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            style={positionStyle}
            title="Send Feedback (Drag to move)"
            type="button"
        >
            <FaCommentAlt className="feedback-icon" />
            <span className="feedback-text">Feedback</span>
        </button>
    );
}

export default FeedbackButton;